import { Directive, ElementRef, NgZone, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { Subscription } from 'rxjs';

/**
 * Holds the page content at its current height while the router swaps pages.
 *
 * The new page usually renders shorter than the one it replaces, and the scroll position is only
 * reset to the top a frame or two after the swap. In between, the browser clamps the scroll to the
 * shorter document and the footer jumps up into view, then away again. On a phone the swap lands
 * well over 500ms after the tap, so that jump counts towards CLS, and CrUX charges it to the page
 * the visitor landed on. Holding the height keeps the footer below the fold until the scroll has
 * been reset, then the hold is dropped.
 *
 * Phones and tablets only, and only when the path changes, so filtering a listing (query or
 * fragment only) still resizes the page straight away, inside the input window.
 */
@Directive({
    standalone: true,
    selector: '[navigationHeightHold]'
})
export class NavigationHeightHoldDirective implements OnInit, OnDestroy {
    private readonly router = inject(Router);
    private readonly app = inject(App);
    private readonly client = inject(Client);
    private readonly elementRef = inject(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly zone = inject(NgZone);

    /**
     * Fallback release, for navigations that never move the scroll position
     */
    private readonly releaseTimeout = 1000;

    private subscription?: Subscription;
    private releaseTimer?: ReturnType<typeof setTimeout>;
    private removeScrollListener?: () => void;
    private isHolding = false;

    ngOnInit(): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }
        this.subscription = this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                if (this.getPath(event.url) !== this.getPath(this.router.url)) {
                    this.hold();
                }
            } else if (
                event instanceof NavigationEnd ||
                event instanceof NavigationCancel ||
                event instanceof NavigationError
            ) {
                this.scheduleRelease();
            }
        });
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
        this.release();
    }

    private hold(): void {
        if (!this.client.isS && !this.client.isM) {
            return;
        }
        this.clearRelease();
        const element = this.elementRef.nativeElement as HTMLElement;
        this.renderer.setStyle(element, 'min-height', `${element.offsetHeight}px`);
        this.isHolding = true;
    }

    /**
     * Releases on the scroll reset that follows the swap, or after the fallback timeout
     */
    private scheduleRelease(): void {
        if (!this.isHolding) {
            return;
        }
        this.clearRelease();
        this.zone.runOutsideAngular(() => {
            this.removeScrollListener = this.renderer.listen('window', 'scroll', () => this.release());
            this.releaseTimer = setTimeout(() => this.release(), this.releaseTimeout);
        });
    }

    private release(): void {
        this.clearRelease();
        if (this.isHolding) {
            this.renderer.removeStyle(this.elementRef.nativeElement, 'min-height');
            this.isHolding = false;
        }
    }

    private clearRelease(): void {
        clearTimeout(this.releaseTimer);
        this.releaseTimer = undefined;
        this.removeScrollListener?.();
        this.removeScrollListener = undefined;
    }

    private getPath(url: string): string {
        return url.split(/[?#]/)[0];
    }
}
